import { Box } from "@mui/material";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import BriaCanvas from "../../../components/common/Galleries/BriaCanvas";
import { useAppStore } from "../../../hooks/useStores";
import MaskEditorControlsRow from "./ControlPanel/ControlPanel";
import styles from "./InlineMaskEditor.module.scss";

export interface InlineMaskEditorProps {
	show: boolean;
	image: string;
	onClose?: () => void;
	onMaskModeChange?: (enabled: boolean) => void;
	showCanvas?: boolean;
	showToggle?: boolean;
	initialMaskEditorEnabled?: boolean;
}

const InlineMaskEditor = ({
	show,
	image,
	onMaskModeChange,
	showCanvas = true,
	showToggle = true,
	initialMaskEditorEnabled = false,
}: InlineMaskEditorProps) => {
	const { fiboStore } = useAppStore();
	const { fiboEditStore } = fiboStore;
	const maskEditorStore = fiboStore.fiboEditStore.maskEditorStore;

	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const canvasOverlayRef = useRef<HTMLCanvasElement | null>(null);
	const maskLoadedForSessionRef = useRef(false);
	const [maskEditorEnabled, setMaskEditorEnabled] = useState(initialMaskEditorEnabled);

	useEffect(() => {
		if (showCanvas) {
			runInAction(() => {
				maskEditorStore.brushCanvasRefs[0] = {
					canvasRef,
					canvasOverlayRef,
				};
			});
		}

		return () => {
			if (showCanvas) {
				maskEditorStore.resetBrushConfigs();
			}
		};
	}, [showCanvas, maskEditorStore]);

	// Try to load mask when editor opens or canvas becomes ready, or clear it when mask is removed
	useEffect(() => {
		const loadOrClearMask = async () => {
			if (show && showCanvas) {
				if (fiboEditStore.config.mask && !maskLoadedForSessionRef.current) {
					// Load mask if one exists
					await maskEditorStore.loadMaskOntoCanvas(fiboEditStore.config.mask);
					maskLoadedForSessionRef.current = true;
				} else if (!fiboEditStore.config.mask && maskLoadedForSessionRef.current) {
					// Clear canvas if mask was removed
					maskEditorStore.clearCanvas();
					maskEditorStore.resetBrushConfigs();
					maskLoadedForSessionRef.current = false;
				}
			}
		};

		if (show && showCanvas) {
			if (!fiboEditStore.config.mask) {
				maskLoadedForSessionRef.current = false; // Reset for new session without mask
			}
			loadOrClearMask();
		}
	}, [show, showCanvas, fiboEditStore.config.mask, maskEditorStore]);

	// Set original image dimensions for proper mask scaling
	useEffect(() => {
		if (image && showCanvas) {
			maskEditorStore.setOriginalImageDimensions(image);
		}
	}, [image, showCanvas, maskEditorStore]);

	// Sync internal state with external prop changes
	useEffect(() => {
		if (maskEditorEnabled !== initialMaskEditorEnabled) {
			setMaskEditorEnabled(initialMaskEditorEnabled);
		}
	}, [initialMaskEditorEnabled]);

	// Notify parent when mask mode changes (but avoid infinite loops)
	useEffect(() => {
		if (maskEditorEnabled !== initialMaskEditorEnabled) {
			onMaskModeChange?.(maskEditorEnabled);
		}
	}, [maskEditorEnabled]);

	const handleMaskEditorToggle = (enabled: boolean) => {
		setMaskEditorEnabled(enabled);
	};

	if (!show) return null;
	const renderMaskEditorControls = () => (
		<MaskEditorControlsRow
			brush={{
				size: maskEditorStore.brushSize,
				type: maskEditorStore.maskType,
				onChange: (size, type) => {
					maskEditorStore.setBrushSize(size);
					maskEditorStore.setMaskType(type);
				},
			}}
			onResetMask={maskEditorStore.resetBrushConfigs}
			maskEditorEnabled={maskEditorEnabled}
			onMaskEditorToggle={handleMaskEditorToggle}
			showToggle={showToggle}
			className={styles.controlsRowWidth}
		/>
	);
	// When only showing the control bar (not in canvas mode)
	if (!showCanvas) {
		return renderMaskEditorControls();
	}

	// Full editor mode with canvas
	return (
		<Box className={styles.inlineMaskEditor}>
			{image && (
				<BriaCanvas
					className={styles.canvasContainer}
					url={image}
					canvasRef={(ref: HTMLCanvasElement | null) => {
						canvasRef.current = ref;
					}}
					canvasOverlayRef={(ref: HTMLCanvasElement | null) => {
						canvasOverlayRef.current = ref;
					}}
					brushConfigs={maskEditorStore.brushConfigs}
					onCanvasSizeUpdate={() => {}}
				/>
			)}

			{showToggle && renderMaskEditorControls()}
		</Box>
	);
};

export default observer(InlineMaskEditor);
